import { DealStageRequest } from './../../entities/deal/DealStageResponse';
import { DealAPI } from '@business/api/deal';
import {
  CompactDealBoardResponse,
  DealBoardId,
  DealBoardResponse,
  DealId,
  DealStageId,
  DealStatus,
  CompactDealResponse,
} from '@business/entities/deal';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation, showConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { TeamId } from '@business/entities/team';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';

export class DealBoardBloc extends BaseBloc {
  private readonly dealBoardRepo = new Repository<CompactDealBoardResponse | DealBoardResponse>({
    getItemId: item => item.id,
  });

  readonly boards$ = this.dealBoardRepo.items$.pipe(map(items => R.sortBy(items, item => item.name.toLowerCase())));
  readonly selectBoard = this.dealBoardRepo.selectItem;

  onReset() {
    this.dealBoardRepo.reset();
  }

  fetchDealBoards = () => {
    return DealAPI.fetchDealBoards().pipe(
      tap(boards =>
        boards.map(board => {
          const boardItem = this.dealBoardRepo.getItem(board.id);
          if (boardItem) {
            this.dealBoardRepo.updateOne({ id: board.id, changes: () => ({ name: board.name, settings: board.settings, assignee: board.assignee }) });
          } else {
            this.dealBoardRepo.addOne(board);
          }
        }),
      ),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  fetchDealBoard = (id: DealBoardId, status: DealStatus) => {
    return DealAPI.fetchDealBoard(id, status).pipe(
      this.dealBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  createDealBoard = (name: string, teams: TeamId[], stages: DealStageRequest[], assigneeId: UserId | null = null) => {
    return DealAPI.createDealBoard(name, teams, stages, assigneeId).pipe(
      this.dealBoardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_deal_board'),
      }),
    );
  };

  updateDealBoard = (id: DealBoardId, name: string, teams: TeamId[], stages: DealStageRequest[], assigneeId: UserId | null = null) => {
    return DealAPI.updateDealBoard(id, name, teams, stages, assigneeId).pipe(
      this.dealBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_deal_board'),
      }),
    );
  };

  deleteDealBoard = (id: DealBoardId) => {
    return showDeleteConfirmation('Delete Deal Board', 'Do you really want to remove this deal board?').pipe(
      switchMap(() => DealAPI.deleteDealBoard(id)),
      this.dealBoardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_deal_board'),
      }),
    );
  };

  orderDeals = (dealBoardId: DealBoardId, stageId: DealStageId, deals: DealId[]) => {
    return DealAPI.sortBoardDeals(dealBoardId, { stageId: stageId, deals: deals }).pipe(
      //   this.dealBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('deal_order_error'),
      }),
    );
  };

  onStageChange = (boardId: string, deal: CompactDealResponse, stageId: DealStageId) => {
    return DealAPI.updateDealStageMin(deal.id, stageId).pipe(
      //   this.dealBoardRepo.ops.upsertOne(item => ({ item })),
      tap(() => {
        const board = this.dealBoardRepo.getItem(boardId)!;
        let currentStage = board.stages.find(stage => stage.id === deal.stageId)!;
        const newStage = board.stages.find(stage => stage.id === stageId)!;
        const stages = board.stages;
        currentStage = { ...currentStage, deals: currentStage.deals.filter(d => d.id !== deal.id) };
        // currentStage.deals = currentStage.deals.filter(d => d.id !== deal.id);
        const menuOrder = R.max(newStage.deals.map(d => d.menuOrder))! + 1;
        newStage.deals.push({ ...deal, menuOrder, stageId: stageId });
        const newStages = stages
          .map(stage => (stage.id === currentStage.id ? currentStage : stage))
          .map(stage => (stage.id === newStage.id ? newStage : stage));
        this.dealBoardRepo.updateOne({ id: boardId, changes: () => ({ stages: newStages }) });
      }),
      handleMessage({
        error: requestMessage('deal_order_error'),
      }),
    );
  };

  // Local-only: move a deal to a different stage within whichever loaded board it currently sits in.
  // Used when a server-side action (e.g. status → stage automation) changed the deal's stage and the
  // board needs to reflect it immediately, without a refetch.
  moveDealToStageInBoards = (dealId: DealId, newStageId: DealStageId) => {
    this.dealBoardRepo.items.forEach(board => {
      const fullBoard = board as DealBoardResponse;
      if (!fullBoard.stages?.length || !('deals' in fullBoard.stages[0])) return;

      let movingDeal: CompactDealResponse | undefined;
      fullBoard.stages.forEach(stage => {
        const found = stage.deals.find(d => d.id === dealId);
        if (found) movingDeal = found;
      });
      if (!movingDeal || String(movingDeal.stageId) === String(newStageId)) return;

      const targetStage = fullBoard.stages.find(s => String(s.id) === String(newStageId));
      if (!targetStage) return; // target stage is not on this board

      const menuOrder = (R.max(targetStage.deals.map(d => d.menuOrder)) ?? -1) + 1;
      const updatedDeal = { ...movingDeal, stageId: newStageId, menuOrder };

      const updatedStages = fullBoard.stages.map(stage => {
        if (String(stage.id) === String(newStageId)) {
          return { ...stage, deals: [...stage.deals.filter(d => d.id !== dealId), updatedDeal] };
        }
        return { ...stage, deals: stage.deals.filter(d => d.id !== dealId) };
      });

      this.dealBoardRepo.updateOne({ id: board.id, changes: () => ({ stages: updatedStages }) });
    });
  };

  // Local-only: remove a deal from every loaded board, e.g. after server-side automation
  // (Salesperson → Board) moved it to a board that isn't currently loaded.
  removeDealFromBoards = (dealId: DealId) => {
    this.dealBoardRepo.items.forEach(board => {
      const fullBoard = board as DealBoardResponse;
      if (!fullBoard.stages?.length || !('deals' in fullBoard.stages[0])) return;
      if (!fullBoard.stages.some(stage => stage.deals.some(d => d.id === dealId))) return;
      const updatedStages = fullBoard.stages.map(stage => ({ ...stage, deals: stage.deals.filter(d => d.id !== dealId) }));
      this.dealBoardRepo.updateOne({ id: board.id, changes: () => ({ stages: updatedStages }) });
    });
  };

  patchDealInBoards = (dealId: DealId, changes: Partial<CompactDealResponse>) => {
    this.dealBoardRepo.items.forEach(board => {
      const fullBoard = board as DealBoardResponse;
      if (!fullBoard.stages?.length || !('deals' in fullBoard.stages[0])) return;
      const updatedStages = fullBoard.stages.map(stage => ({
        ...stage,
        deals: stage.deals.map(d => (d.id === dealId ? { ...d, ...changes } : d)),
      }));
      this.dealBoardRepo.updateOne({ id: board.id, changes: () => ({ stages: updatedStages }) });
    });
  };

  duplicateDealBoard = (id: DealBoardId) => {
    return showConfirmation('Duplicate Deal Board', 'Do you really want to duplicate this deal board?', { btnYesLabel: 'Duplicate' }).pipe(
      switchMap(() => DealAPI.duplicateDealBoard(id)),
      this.dealBoardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('duplicate_deal_board'),
      }),
    );
  };
}
